import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useRequests, saveRequests, nextRequestId, nowTs, todayPacific, lastActivity,
  isRequestOpen, isRequestClosed, REQ_STATUSES, REQ_METHODS, REQ_TYPES, PRIORITIES,
  type CsrRequest,
} from '../lib/csrRequestsLive';
import { useCustomers } from '../lib/csrSuite';

type ViewKey = 'all' | 'open' | 'overdue' | 'today' | 'unassigned' | 'completed';

const NEW_REQ = (): CsrRequest => ({ id: '', desc: '', status: 'New', priority: 'Normal', types: [], date: todayPacific(), history: [] });

function statusColor(s?: string): string {
  if (s === 'Completed') return 'var(--gn)';
  if (s === 'Cancelled') return 'var(--tx3)';
  if (s === 'On Hold' || s === 'Waiting on Client') return 'var(--yl)';
  return 'var(--ac)';
}

export default function RequestLog() {
  const { requests, loading, live, error } = useRequests();
  const { customers } = useCustomers();
  const [params] = useSearchParams();
  const [view, setView] = useState<ViewKey>('open');
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState<CsrRequest | null>(null);
  const [actingAs, setActingAs] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [msg, setMsg] = useState('');
  const today = todayPacific();

  useEffect(() => {
    const c = params.get('client');
    if (c && !draft) setDraft({ ...NEW_REQ(), client: c });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const team = useMemo(() => {
    const set2 = new Set<string>();
    requests.forEach((r) => { if (r.assignee) set2.add(r.assignee); if (r.loggedBy) set2.add(r.loggedBy); });
    return [...set2].sort();
  }, [requests]);

  const counts = useMemo(() => {
    const open = requests.filter((r) => isRequestOpen(r.status));
    return {
      all: requests.length,
      open: open.length,
      overdue: open.filter((r) => r.due && r.due < today).length,
      today: open.filter((r) => r.due === today).length,
      unassigned: open.filter((r) => !r.assignee).length,
      completed: requests.filter((r) => r.status === 'Completed').length,
    };
  }, [requests, today]);

  const shown = useMemo(() => {
    let list = requests;
    if (view === 'open') list = list.filter((r) => isRequestOpen(r.status));
    else if (view === 'overdue') list = list.filter((r) => isRequestOpen(r.status) && r.due && r.due < today);
    else if (view === 'today') list = list.filter((r) => isRequestOpen(r.status) && r.due === today);
    else if (view === 'unassigned') list = list.filter((r) => isRequestOpen(r.status) && !r.assignee);
    else if (view === 'completed') list = list.filter((r) => r.status === 'Completed');
    const t = q.trim().toLowerCase();
    if (t) list = list.filter((r) => `${r.desc} ${r.client || ''} ${r.id} ${r.assignee || ''} ${(r.types || []).join(' ')} ${r.ref || ''}`.toLowerCase().includes(t));
    return [...list].sort((a, b) => (a.due || '9999').localeCompare(b.due || '9999') || lastActivity(b).localeCompare(lastActivity(a)));
  }, [requests, view, q, today]);

  const set = (k: keyof CsrRequest) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setDraft((p) => (p ? { ...p, [k]: e.target.value } : p));
  const toggleType = (t: string) =>
    setDraft((p) => { if (!p) return p; const has = (p.types || []).includes(t); return { ...p, types: has ? (p.types || []).filter((x) => x !== t) : [...(p.types || []), t] }; });

  function addNote() {
    if (!note.trim() || !draft) return;
    const who = actingAs || draft.assignee || draft.loggedBy || 'Team';
    setDraft({ ...draft, history: [...(draft.history || []), { ts: nowTs(), who, act: '📝 ' + note.trim() }] });
    setNote('');
  }

  async function onSave() {
    if (!draft) return;
    if (!draft.desc.trim()) { setStatus('error'); setMsg('Description is required so the request can be identified.'); return; }
    setStatus('saving');
    try {
      const d: CsrRequest = { ...draft, desc: draft.desc.trim() };
      const actor = actingAs || d.assignee || d.loggedBy || 'Team';
      let next: CsrRequest[];
      if (!d.id) {
        d.id = await nextRequestId();
        d.created = nowTs();
        d.history = [...(d.history || []), { ts: nowTs(), who: actor, act: 'Request created' }];
        next = [d, ...requests];
      } else {
        const prev = requests.find((r) => r.id === d.id);
        const hist = [...(d.history || [])];
        if (prev && prev.status !== d.status) hist.push({ ts: nowTs(), who: actor, act: `Status: ${prev.status || '—'} → ${d.status}` });
        if (prev && (prev.assignee || '') !== (d.assignee || '')) hist.push({ ts: nowTs(), who: actor, act: `Reassigned: ${prev.assignee || '—'} → ${d.assignee || '—'}` });
        if (isRequestClosed(d.status) && !d.closedDate) { d.closedDate = today; d.closedBy = actor; }
        d.history = hist;
        next = requests.map((r) => (r.id === d.id ? d : r));
      }
      await saveRequests(next);
      setDraft(d);
      setStatus('saved'); setMsg(`Saved ${d.id}`);
      setTimeout(() => setStatus('idle'), 1600);
    } catch (e) {
      setStatus('error');
      setMsg((e as Error)?.message?.includes('permission') ? 'Sign in with your Microflex account to save.' : ((e as Error)?.message || 'Save failed'));
    }
  }

  const chips: [ViewKey, string, number][] = [
    ['open', 'Open', counts.open], ['overdue', 'Overdue', counts.overdue], ['today', 'Due today', counts.today],
    ['unassigned', 'Unassigned', counts.unassigned], ['completed', 'Completed', counts.completed], ['all', 'All', counts.all],
  ];

  return (
    <>
      <style>{css}</style>
      <div className="page-head">
        <div>
          <div className="page-title">Request Log</div>
          <div className="page-sub">{requests.length} requests · shared with the estimating suite. {live ? 'Live' : loading ? 'Loading…' : 'Offline'}.</div>
        </div>
        <button className="btn small primary rl-new" onClick={() => { setDraft(NEW_REQ()); setActingAs(''); }}>＋ New Request</button>
      </div>

      {error && <div className="rl-warn">Couldn’t load the request log: {error}</div>}

      <div className="rl-views">
        {chips.map(([k, label, n]) => (
          <div key={k} className={`rl-chip${view === k ? ' active' : ''}`} onClick={() => setView(k)}>{label} <span>{n}</span></div>
        ))}
        <input className="rl-search" placeholder="Search description, client, ticket, owner…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <div className="rl-grid">
        <div className="card rl-list">
          {loading ? <div className="rl-empty">Loading…</div> : shown.length === 0 ? <div className="rl-empty">No requests in this view.</div> :
            shown.map((r) => {
              const overdue = isRequestOpen(r.status) && r.due && r.due < today;
              return (
                <div key={r.id} className={`rl-row${draft?.id === r.id ? ' active' : ''}`} onClick={() => { setDraft({ ...r }); setActingAs(''); }}>
                  <div className="rl-desc">{r.desc || '(no description)'}</div>
                  <div className="rl-meta">
                    <span className="rl-badge" style={{ color: statusColor(r.status) }}>{r.status}</span>
                    {[r.client, r.id, r.assignee || 'Unassigned'].filter(Boolean).join(' · ')}
                    {r.due ? <span className={overdue ? 'rl-over' : ''}> · due {r.due.slice(5)}</span> : null}
                  </div>
                </div>
              );
            })}
        </div>

        <div className="card rl-detail">
          {!draft ? (
            <div className="rl-empty big">🗂<div>Select a request to view &amp; edit,<br />or log a new one.</div></div>
          ) : (
            <>
              <div className="rl-dh">
                <h2>{draft.id || 'New Request'}</h2>
                <div className={`rl-status ${status}`}>{status === 'saving' ? 'Saving…' : status === 'saved' ? `✔ ${msg}` : status === 'error' ? `⚠ ${msg}` : ''}</div>
              </div>
              <div className="rl-fields">
                <label className="rl-f"><span>Client</span><input list="rl-custlist" value={draft.client || ''} onChange={set('client')} /></label>
                <datalist id="rl-custlist">{customers.map((c) => <option key={c.code || c.n} value={c.n} />)}</datalist>
                <label className="rl-f"><span>Contact</span><input value={draft.contact || ''} onChange={set('contact')} /></label>
                <label className="rl-f"><span>Logged by</span><input list="rl-team" value={draft.loggedBy || ''} onChange={set('loggedBy')} /></label>
                <label className="rl-f"><span>Assigned to</span><input list="rl-team" value={draft.assignee || ''} onChange={set('assignee')} /></label>
                <datalist id="rl-team">{team.map((t) => <option key={t} value={t} />)}</datalist>
                <label className="rl-f"><span>Method</span><select value={draft.method || ''} onChange={set('method')}><option value="">—</option>{REQ_METHODS.map((m) => <option key={m}>{m}</option>)}</select></label>
                <label className="rl-f"><span>Priority</span><select value={draft.priority || 'Normal'} onChange={set('priority')}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></label>
                <label className="rl-f"><span>Status</span><select value={draft.status || 'New'} onChange={set('status')}>{REQ_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
                <label className="rl-f"><span>Date received</span><input type="date" value={draft.date || ''} onChange={set('date')} /></label>
                <label className="rl-f"><span>Due / follow-up</span><input type="date" value={draft.due || ''} onChange={set('due')} /></label>
                <label className="rl-f"><span>Scheduled run date</span><input type="date" value={draft.runDate || ''} onChange={set('runDate')} /></label>
                <label className="rl-f"><span>Reference #</span><input value={draft.ref || ''} onChange={set('ref')} /></label>
              </div>

              <div className="rl-typerow">
                <span className="rl-l">What are they asking for?</span>
                <div className="rl-types">
                  {REQ_TYPES.map((t) => (
                    <label key={t} className={`rl-type${(draft.types || []).includes(t) ? ' on' : ''}`}>
                      <input type="checkbox" checked={(draft.types || []).includes(t)} onChange={() => toggleType(t)} /> {t}
                    </label>
                  ))}
                </div>
              </div>

              <label className="rl-f wide"><span>Description — required</span><textarea rows={3} value={draft.desc} onChange={set('desc')} placeholder="e.g. 300 flexo samples of Zero Brass — press check same run — due 8/19" /></label>
              <label className="rl-f wide"><span>Next action</span><input value={draft.nextAction || ''} onChange={set('nextAction')} placeholder="e.g. Request art + PO from client by 7/20" /></label>
              <label className="rl-f wide"><span>Internal notes</span><input value={draft.notes || ''} onChange={set('notes')} /></label>

              <div className="rl-subhead">Activity history
                <span className="rl-actas">Update as <input placeholder="your name" value={actingAs} onChange={(e) => setActingAs(e.target.value)} /></span>
              </div>
              <div className="rl-hist">
                {(draft.history || []).length === 0 ? <div className="rl-empty" style={{ padding: 12 }}>No activity yet.</div> :
                  (draft.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="rl-hrow"><span className="rl-hwho">{h.who || '—'}</span><span className="rl-hact">{h.act}</span><span className="rl-hts">{(h.ts || '').slice(0, 16).replace('T', ' ')}</span></div>
                  ))}
              </div>
              <div className="rl-noterow">
                <input placeholder="Leave a note for the team…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} />
                <button className="btn small" onClick={addNote}>＋ Add note</button>
              </div>

              <div className="rl-savebar">
                <button className="btn small primary" onClick={onSave} disabled={status === 'saving'}>💾 Save</button>
                <span className="rl-lock">🔒 Activity is append-only — logged with who &amp; when.</span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

const css = `
.rl-new{width:auto;padding:9px 16px}
.rl-warn{background:rgba(234,179,8,.12);border:1px solid rgba(234,179,8,.4);color:var(--yl);border-radius:10px;padding:10px 14px;font-size:13px;margin:10px 0}
.rl-views{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin:12px 0}
.rl-chip{padding:6px 12px;border-radius:999px;border:1px solid var(--bdr);background:var(--bg2);font-size:12px;font-weight:600;cursor:pointer;color:var(--tx2)}
.rl-chip.active{background:rgba(0,168,207,.14);border-color:rgba(0,168,207,.45);color:var(--ac)}
.rl-chip span{opacity:.6;margin-left:3px}
.rl-search{margin-left:auto;max-width:280px}
.rl-grid{display:grid;grid-template-columns:340px 1fr;gap:16px;align-items:start}
@media(max-width:960px){.rl-grid{grid-template-columns:1fr}}
.rl-list{padding:8px;max-height:76vh;overflow:auto}
.rl-row{padding:10px 12px;border-radius:8px;cursor:pointer;border:1px solid transparent}
.rl-row:hover{background:var(--bg2)}
.rl-row.active{background:rgba(0,168,207,.1);border-color:rgba(0,168,207,.4)}
.rl-desc{font-weight:700;font-size:13px;color:var(--tx);line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.rl-meta{font-size:11px;color:var(--tx3);margin-top:3px}
.rl-badge{font-weight:700;margin-right:6px}
.rl-over{color:var(--rd);font-weight:700}
.rl-detail{padding:18px;min-height:340px}
.rl-empty{color:var(--tx3);text-align:center;padding:30px;font-size:13px}
.rl-empty.big{font-size:34px;display:flex;flex-direction:column;align-items:center;gap:12px}
.rl-empty.big div{font-size:13px}
.rl-dh{display:flex;align-items:center;margin-bottom:12px}
.rl-dh h2{margin:0;font-size:18px}
.rl-status{margin-left:auto;font-size:12px;font-weight:600}
.rl-status.saved{color:var(--gn)}.rl-status.error{color:var(--rd)}.rl-status.saving{color:var(--ac)}
.rl-fields{display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px 14px}
@media(max-width:680px){.rl-fields{grid-template-columns:1fr 1fr}}
.rl-f{display:flex;flex-direction:column;gap:5px}
.rl-f.wide{margin-top:12px}
.rl-f>span,.rl-l{font-size:11px;color:var(--tx3);font-weight:600}
.rl-typerow{margin-top:14px}
.rl-types{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}
.rl-type{font-size:12px;padding:4px 10px;border:1px solid var(--bdr);border-radius:999px;cursor:pointer;color:var(--tx2)}
.rl-type.on{background:rgba(0,168,207,.14);border-color:rgba(0,168,207,.45);color:var(--ac)}
.rl-type input{display:none}
.rl-subhead{display:flex;align-items:center;gap:10px;font-weight:700;font-size:13px;margin:18px 0 6px;color:var(--tx2)}
.rl-actas{margin-left:auto;font-size:11px;color:var(--tx3);font-weight:500;display:flex;align-items:center;gap:6px}
.rl-actas input{width:130px;padding:5px 9px;font-size:12px}
.rl-hist{max-height:180px;overflow:auto;border:1px solid var(--bdr);border-radius:10px;padding:6px}
.rl-hrow{display:flex;gap:10px;padding:5px 6px;font-size:12px;border-bottom:1px solid var(--bdr)}
.rl-hrow:last-child{border-bottom:none}
.rl-hwho{font-weight:700;color:var(--tx2);min-width:80px}
.rl-hact{flex:1;color:var(--tx)}
.rl-hts{color:var(--tx3);white-space:nowrap}
.rl-noterow{display:flex;gap:8px;margin-top:8px}
.rl-noterow input{flex:1}
.rl-noterow .btn{width:auto;padding:8px 14px}
.rl-savebar{display:flex;align-items:center;gap:12px;border-top:1px solid var(--bdr);padding-top:14px;margin-top:14px}
.rl-savebar .btn{width:auto;padding:9px 18px}
.rl-lock{font-size:11px;color:var(--tx3)}
`;
